import {businessCalendarDate, businessTimestampFromDate} from 'src/api/business-time'

import type {RentalAsset} from '../assets/rental-assets/schemas'
import type {Worker} from '../assets/workers/schemas'
import {commitJobToTimeline} from '../dispatch-timeline/allocation'
import type {
    DispatchAssignment,
    DispatchResource,
    DispatchTimelineData,
    JobAllocation,
    JobRequirement,
    ScheduleDate,
    UnassignedJob,
} from '../dispatch-timeline/types'
import {buildWorkDaySlots} from '../dispatch-timeline/work-day'
import type {JobDetail, JobDetailRequirement} from '../jobs/types'
import type {DispatchBoardScenario} from './dispatch-board-scenario'
import {mapJobScheduleToTimeline} from './job-schedule-to-timeline'

export function deriveInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) {
        return '??'
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export function formatJobCode(jobId: string): string {
    return `JOB-${jobId.replace(/-/g, '').slice(-4).toUpperCase()}`
}

function mapWorkerToResource(worker: Worker): DispatchResource {
    return {
        id: worker.id,
        name: worker.name,
        initials: deriveInitials(worker.name),
    }
}

function mapRentalAssetToResource(asset: RentalAsset): DispatchResource {
    const name = asset.name ?? asset.rentalAssetTypeName
    return {
        id: asset.id,
        name,
        initials: deriveInitials(name),
        rentalAssetTypeId: asset.rentalAssetTypeId,
        rentalAssetTypeName: asset.rentalAssetTypeName,
        isUniquelyIdentified: asset.isUniquelyIdentified,
    }
}

function mapRequirement(requirement: JobDetailRequirement): JobRequirement {
    const kind = requirement.requirementType
    const label = kind === 'worker' ? 'Drivers' : (requirement.rentalAssetTypeName ?? 'Equipment')

    return {
        id: requirement.id,
        kind,
        label,
        quantity: requirement.quantity,
        rentalAssetTypeId: requirement.rentalAssetTypeId ?? undefined,
    }
}

/** Links flat API assignments to requirements using the same rules as job details fulfillment. */
export function inferAllocations(job: JobDetail): JobAllocation[] {
    const allocations: JobAllocation[] = []
    const workerAssignments = job.assignments.filter((entry) => entry.workerId != null)
    const rentalAssignments = job.assignments.filter((entry) => entry.rentalAssetId != null)

    for (const requirement of job.requirements) {
        if (requirement.requirementType === 'worker') {
            for (let index = 0; index < requirement.quantity; index++) {
                const assignment = workerAssignments.shift()
                if (!assignment?.workerId) {
                    break
                }
                allocations.push({
                    requirementId: requirement.id,
                    resourceId: assignment.workerId,
                    resourceName: assignment.workerName ?? 'Worker',
                    kind: 'worker',
                })
            }
            continue
        }

        const matching = rentalAssignments.filter(
            (entry) => entry.rentalAssetTypeId === requirement.rentalAssetTypeId,
        )
        for (let index = 0; index < requirement.quantity; index++) {
            const assignment = matching[index]
            if (!assignment?.rentalAssetId) {
                break
            }
            allocations.push({
                requirementId: requirement.id,
                resourceId: assignment.rentalAssetId,
                resourceName: assignment.rentalAssetName ?? 'Equipment',
                kind: 'rental_asset',
            })
        }
    }

    return allocations
}

function boardIdentityFromJobDetail(
    job: JobDetail,
    projectNamesById: ReadonlyMap<string, string>,
): DispatchBoardScenario | undefined {
    const projectName = projectNamesById.get(job.projectId)
    const siteName = job.locations[0]?.name
    const description = job.serviceTypeName.trim() || undefined

    if (!projectName && !siteName && !description) {
        return undefined
    }

    return {projectName, siteName, description}
}

export function mapJobDetailToUnassignedJob(
    job: JobDetail,
    anchorDate: Date = businessCalendarDate(),
    projectNamesById: ReadonlyMap<string, string> = new Map(),
): UnassignedJob {
    const requirements = job.requirements.map(mapRequirement)
    const {scheduleKind, span} = mapJobScheduleToTimeline(job, anchorDate)

    return {
        id: job.id,
        jobCode: job.displayId ?? formatJobCode(job.id),
        label: job.name,
        customer: job.customerName,
        scheduleKind,
        span,
        schedule: {
            startDate: job.startDate,
            startTime: job.startTime,
            endDate: job.endDate,
            endTime: job.endTime,
        },
        requirements,
        allocations: inferAllocations(job),
        warnings: job.requirements.length === 0 ? ['No requirements defined'] : undefined,
        boardScenario: boardIdentityFromJobDetail(job, projectNamesById),
    }
}

function shouldCommitToBoard(_job: UnassignedJob): boolean {
    void _job
    return false
}

export function buildTodayScheduleDate(): ScheduleDate {
    const date = businessCalendarDate()
    const weekdayShort = date.toLocaleDateString('en-US', {weekday: 'short'})
    const monthLabel = date.toLocaleDateString('en-US', {month: 'short'})
    const dayOfMonth = date.getDate()

    return {
        label: date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }),
        weekdayShort,
        dayOfMonth,
        monthLabel,
        isToday: true,
    }
}

export function mapToDispatchTimelineData(input: {
    workers: Worker[]
    rentalAssets: RentalAsset[]
    jobs: JobDetail[]
    projects?: Array<{id: string; name: string}>
}): DispatchTimelineData {
    const projectNamesById = new Map(
        (input.projects ?? []).map((project) => [project.id, project.name] as const),
    )
    const unassignedJobs: UnassignedJob[] = []
    const assignments: DispatchAssignment[] = []

    for (const job of input.jobs) {
        if (job.requirements.length === 0 && job.assignments.length === 0) {
            continue
        }

        const anchorDate = businessCalendarDate()
        const boardJob = mapJobDetailToUnassignedJob(job, anchorDate, projectNamesById)
        if (shouldCommitToBoard(boardJob)) {
            assignments.push(commitJobToTimeline(boardJob))
        } else {
            unassignedJobs.push(boardJob)
        }
    }

    return {
        fieldLabel: 'FIELD OPERATIONS',
        scheduleDate: buildTodayScheduleDate(),
        timeSlots: buildWorkDaySlots(businessTimestampFromDate(new Date()).getHours()),
        resources: input.workers.map(mapWorkerToResource),
        equipment: input.rentalAssets.map(mapRentalAssetToResource),
        unassignedJobs,
        assignments,
        hourlyLoad: [],
    }
}

export function allocationsToAssignmentValues(
    allocations: JobAllocation[],
): {workerId: string | null; rentalAssetId: string | null; days?: JobAllocation['days']}[] {
    return allocations.map((allocation) => ({
        workerId: allocation.kind === 'worker' ? allocation.resourceId : null,
        rentalAssetId: allocation.kind === 'rental_asset' ? allocation.resourceId : null,
        ...(allocation.kind === 'worker' && allocation.days ? {days: allocation.days} : {}),
    }))
}

export function extractAllocationsFromBoardState(
    data: DispatchTimelineData,
    jobId: string,
): JobAllocation[] {
    const unassigned = data.unassignedJobs.find((entry) => entry.id === jobId)
    if (unassigned) {
        return unassigned.allocations
    }

    const committed = data.assignments.find((entry) => entry.sourceJobId === jobId)
    return committed?.allocations ?? []
}
